import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Checkbox } from '../components/ui/checkbox';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Soft countdown helper — shows "Closes Sun 31 May at 5:00 pm" rather than
// a raw timestamp so it reads like a real competition flyer.
function formatCloseTime(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-GB', {
      weekday: 'short', day: 'numeric', month: 'long',
      hour: 'numeric', minute: '2-digit', hour12: true,
    });
  } catch {
    return 'Sun 31 May at 5:00 pm';
  }
}

function ClosedScreen({ closesAt }) {
  return (
    <div className="min-h-[60vh] flex items-center justify-center bg-[#0A0A0A] text-white px-4 py-20" data-testid="sweet-jar-closed">
      <div className="max-w-xl text-center">
        <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#F0A500] block mb-4">
          Glossop Heritage Weekend
        </span>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mb-4">
          Guess the Sweets — now closed
        </h1>
        <p className="text-base text-zinc-300 leading-relaxed mb-2">
          The competition closed at {formatCloseTime(closesAt)}.
        </p>
        <p className="text-sm text-zinc-400 mb-8">
          Thanks for taking part! Winners will be contacted by Glossop &amp; District Round Table once the jar has been counted.
        </p>
        <Link
          to="/"
          data-testid="sweet-jar-home-btn"
          className="inline-block px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] text-[#0A0A0A] font-bold uppercase tracking-wider text-sm"
        >
          Back to Round Table
        </Link>
      </div>
    </div>
  );
}

export default function SweetJarPage() {
  const [info, setInfo] = useState(null);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [guess, setGuess] = useState('');
  const [notRobot, setNotRobot] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(null); // { guess, name }
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/sweet-jar/info`);
        if (!cancelled) setInfo(data);
      } catch {
        if (!cancelled) setInfo({ closes_at: null, closed: false });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const canSubmit = useMemo(() => (
    !!name.trim() && !!email.trim() && !!guess && !!notRobot && !submitting
  ), [name, email, guess, notRobot, submitting]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);
    try {
      const { data } = await axios.post(`${API}/sweet-jar-guess`, {
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim() || null,
        guess: Number(guess),
        consent: true,
      });
      setSubmitted({ name: data.name, guess: data.guess });
      setName(''); setEmail(''); setPhone(''); setGuess(''); setNotRobot(false);
    } catch (err) {
      if (err.response?.status === 403) {
        setError('Competition just closed — thanks for taking part!');
        setInfo((i) => ({ ...(i || {}), closed: true }));
      } else if (err.response?.data?.detail) {
        const d = err.response.data.detail;
        setError(typeof d === 'string' ? d : 'Please check your details and try again.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!info) {
    return <div className="min-h-[60vh] flex items-center justify-center text-zinc-500">Loading…</div>;
  }
  if (info.closed) return <ClosedScreen closesAt={info.closes_at} />;

  return (
    <div data-testid="sweet-jar-page">
      {/* Hard noindex — the page should only be discovered via the printed QR code. */}
      <Helmet>
        <title>Guess the Sweets — Glossop Heritage Weekend</title>
        <meta name="robots" content="noindex, nofollow, noarchive" />
        <meta name="referrer" content="no-referrer" />
      </Helmet>

      <section className="bg-[#0A0A0A] text-white pt-32 pb-12 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <span className="text-xs uppercase tracking-[0.25em] font-bold text-[#F0A500] block mb-3">
            Glossop Heritage Weekend · Guess the Sweets
          </span>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            How many sweets are in the jar?
          </h1>
          <p className="text-base sm:text-lg text-zinc-300 leading-relaxed">
            Take your best guess — the closest entry to the actual count wins. All proceeds go straight back into local Glossop charities and community projects, courtesy of Glossop &amp; District Round Table 916.
          </p>
          <p className="text-xs uppercase tracking-widest text-zinc-500 mt-6">
            Closes {info.closes_at ? formatCloseTime(info.closes_at) : 'Sun 31 May at 5:00 pm'}
          </p>
        </div>
      </section>

      <section className="py-12 sm:py-16 bg-white px-4">
        <div className="max-w-xl mx-auto">
          {submitted ? (
            <div className="p-8 border-l-4 border-[#F0A500] bg-[#FEF7E6]" data-testid="sweet-jar-success">
              <h2 className="text-2xl font-bold text-[#0A0A0A] mb-2">Thanks, {submitted.name}!</h2>
              <p className="text-base text-[#52525B] leading-relaxed mb-4">
                Your guess of <span className="font-bold text-[#0A0A0A]">{submitted.guess}</span> sweets has been logged. We&rsquo;ll get in touch only if you win &mdash; we won&rsquo;t use your details for anything else.
              </p>
              <button
                type="button"
                onClick={() => setSubmitted(null)}
                data-testid="sweet-jar-another-btn"
                className="text-sm font-bold uppercase tracking-wider text-[#F0A500] hover:text-[#D99500]"
              >
                Paid for another go? Submit another guess &rarr;
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="sweet-jar-form">
              <div className="bg-[#FEF7E6] border-l-4 border-[#F0A500] p-4 text-sm text-[#52525B] leading-relaxed">
                Please submit <strong>one guess per paid entry</strong>. We&rsquo;ll only use your details to contact you if you win &mdash; nothing else, no marketing, no sharing.
              </div>

              <div>
                <Label htmlFor="sj-name" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                  Your name <span className="text-[#D62F2F]">*</span>
                </Label>
                <Input id="sj-name" data-testid="sweet-jar-name" required value={name} onChange={(e) => setName(e.target.value)} />
              </div>

              <div>
                <Label htmlFor="sj-email" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                  Email <span className="text-[#D62F2F]">*</span>
                </Label>
                <Input id="sj-email" data-testid="sweet-jar-email" required type="email" value={email} onChange={(e) => setEmail(e.target.value)} />
              </div>

              <div>
                <Label htmlFor="sj-phone" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                  Mobile number <span className="text-[#71717A] font-normal">(preferred &mdash; quicker to contact a winner)</span>
                </Label>
                <Input id="sj-phone" data-testid="sweet-jar-phone" inputMode="tel" value={phone} onChange={(e) => setPhone(e.target.value)} />
              </div>

              <div>
                <Label htmlFor="sj-guess" className="text-sm font-semibold text-[#0A0A0A] mb-1.5 block">
                  Your guess <span className="text-[#D62F2F]">*</span>
                </Label>
                <Input
                  id="sj-guess"
                  data-testid="sweet-jar-guess"
                  required
                  type="number"
                  inputMode="numeric"
                  min="1"
                  max="99999"
                  step="1"
                  placeholder="How many sweets?"
                  value={guess}
                  onChange={(e) => setGuess(e.target.value)}
                />
              </div>

              <label className="flex items-center gap-3 p-4 bg-[#F4F4F5] border border-zinc-200 cursor-pointer select-none" data-testid="sweet-jar-not_robot-wrap">
                <Checkbox checked={notRobot} onCheckedChange={(c) => setNotRobot(c === true)} data-testid="sweet-jar-not_robot" />
                <span className="text-sm font-semibold text-[#0A0A0A]">I&rsquo;m not a robot</span>
              </label>

              {error && <p data-testid="sweet-jar-error" className="text-sm text-red-600 font-semibold">{error}</p>}

              <button
                type="submit"
                disabled={!canSubmit}
                data-testid="sweet-jar-submit"
                className="w-full px-6 py-3 bg-[#F0A500] hover:bg-[#d99400] disabled:opacity-50 disabled:cursor-not-allowed text-[#0A0A0A] font-bold uppercase tracking-wider text-sm transition-colors"
              >
                {submitting ? 'Submitting…' : 'Submit my guess'}
              </button>

              <p className="text-[11px] text-[#71717A] text-center">
                Glossop &amp; District Round Table 916 &middot; Heritage Weekend competition entry
              </p>
            </form>
          )}
        </div>
      </section>
    </div>
  );
}
